import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageHero, Section, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { INDUSTRIES } from "../lib/site";

export default function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      <SeoHead
        title="Industries"
        description="Domain depth across FinTech, Healthcare, E-commerce, Logistics, Education, SaaS and Government. Every engagement is staffed by people who know your industry's vocabulary, regulations, and customers."
        path="/industries"
      />
      <PageHero
        overline="Industries"
        title={<>Domain depth across seven verticals that don't tolerate generic software.</>}
        intro="Each engagement is staffed by people who have already worked inside your industry's vocabulary, regulations, and customer expectations."
        testId="industries-hero"
      />

      <Section className="bg-[#050505]">
        <div className="space-y-px bg-[#27272A]">
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.04 }}
              className="bg-[#050505] hover:bg-[#0A0A0C] transition-colors"
              data-testid={`industry-row-${ind.id}`}
            >
              <Link to={`/industries/${ind.id}`} className="block group">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 p-8 md:p-12 items-center">
                  <div className="md:col-span-2">
                    <p
                      className="font-serif-display italic text-4xl md:text-5xl font-light"
                      style={{ color: "#C2533B" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </p>
                  </div>
                  <div className="md:col-span-7">
                    <p className="overline mb-3" style={{ color: "#71717A" }}>{ind.name}</p>
                    <h3 className="font-serif-display text-2xl md:text-4xl text-white font-light leading-tight mb-4 group-hover:text-zinc-200 transition">
                      {ind.headline}
                    </h3>
                    <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">
                      {ind.description}
                    </p>
                    <p className="mt-6 inline-flex items-center gap-2 text-white text-sm tracking-wide">
                      Explore the practice <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <div className="aspect-[4/3] overflow-hidden bg-[#0A0A0C]">
                      <img
                        src={ind.image}
                        alt={ind.name}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700"
                      />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section className="bg-[#0A0A0C] border-y border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <p className="overline mb-6">A dedicated practice</p>
            <h2
              className="font-serif-display font-light text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(36px, 5vw, 64px)", letterSpacing: "-0.025em" }}
            >
              Don't see your industry? Send a brief. We'll tell you honestly whether we're the right team.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" className="btn-primary" data-testid="industries-page-cta">
              Talk to a Strategist <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
